import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { deactivateSuperuserSuperusersUserIdDelete } from "@/client-admin";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { getErrorDetails } from "@/lib/utils";

export function DeactivateSuperUserDialog({ userId, email }: { userId: number; email: string }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync: deactivateSuperUser, isPending } = useMutation({
    mutationFn: () => deactivateSuperuserSuperusersUserIdDelete({ userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["superusers"] });
      toast({
        title: "Success",
        description: "Superuser deactivated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: getErrorDetails(error, true),
        variant: "destructive",
      });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate Superuser</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to deactivate {email}? They will no longer be able to log in. The account can be
            reactivated later via Edit.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={async () => {
              await deactivateSuperUser();
              setOpen(false);
            }}
            disabled={isPending}
          >
            {isPending ? "Deactivating..." : "Deactivate"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
